// German → English translation for VFF part descriptions ("Benennung").
//
// Part descriptions are stored in German in Athena. When the UI language is set
// to English we translate the human-readable German words while leaving
// technical codes, dimensions, material specs and brand names untouched
// (e.g. "FLRY- 0,50-A-WS", "M14X1,5-10-MAN183-B1", "COROPLAST").
//
// Strategy:
//   1. Try an exact full-string match against KNOWN_DESCRIPTIONS (curated,
//      highest quality for the most common rows).
//   2. Otherwise fall back to token-by-token replacement using TERM_MAP so any
//      description containing known German words still gets partially
//      translated. Unknown tokens (codes, numbers, dimensions) pass through
//      unchanged.

// ── Curated full-string translations for the most common descriptions ──
const KNOWN_DESCRIPTIONS = {
	"MONT-HW  SACHNUMMER FUER VKGR OHNE INHALT":
		"ASSEMBLY HW  PART NUMBER FOR SALES GROUP WITHOUT CONTENT",
	"MONT-HW  SACHNUMMER FUER VERKAUFSGRUPPE 3D GEOMETRIE":
		"ASSEMBLY HW  PART NUMBER FOR SALES GROUP 3D GEOMETRY",
	"KABELSCHALTPLAN  FHS SYSTEMTRAEGER":
		"WIRING DIAGRAM  CAB SYSTEM CARRIER",
	"KABELSCHALTPLAN  RHM RAHMEN-RECHTS":
		"WIRING DIAGRAM  FRAME FRAME-RIGHT",
	"KABELSCHALTPLAN  RHM RAHMEN-LINKS":
		"WIRING DIAGRAM  FRAME FRAME-LEFT",
	"KABELSCHALTPLAN  FHS DACH": "WIRING DIAGRAM  CAB ROOF",
	"KABELSCHALTPLAN  FHS BUG": "WIRING DIAGRAM  CAB FRONT",
	"KABELSCHALTPLAN  FHS RUMPF-RECHTS": "WIRING DIAGRAM  CAB BODY-RIGHT",
	"KABELSCHALTPLAN  RAHMEN RECHTS ETRUCK":
		"WIRING DIAGRAM  FRAME RIGHT ETRUCK",
	"HINWEIS  OHNE TEILE": "NOTE  WITHOUT PARTS",
	"ZSB HALTER": "ASSEMBLY BRACKET",
	"ABWEICHUNGS-ERLAUBNIS": "DEVIATION PERMIT",
};

// ── Token-level German → English dictionary ──
// Keys are compared case-insensitively against whole whitespace-separated
// tokens (surrounding punctuation is preserved).
const TERM_MAP = {
	// Part types / nouns
	"mont-hw": "ASSEMBLY HW",
	sachnummer: "PART NUMBER",
	kabelschaltplan: "WIRING DIAGRAM",
	systemtraeger: "SYSTEM CARRIER",
	klebeband: "ADHESIVE TAPE",
	gewebeklebeband: "FABRIC ADHESIVE TAPE",
	isolierband: "INSULATING TAPE",
	halter: "BRACKET",
	halterung: "MOUNT",
	"el-leitung": "ELECTRICAL WIRE",
	leitung: "WIRE",
	"kt-mutter": "HEX NUT",
	"kt-schraube": "HEX BOLT",
	blechschraube: "SHEET METAL SCREW",
	schnappmutter: "SNAP NUT",
	schweissmutter: "WELD NUT",
	"blind-einnietmutter": "BLIND RIVET NUT",
	buchsenkontakt: "SOCKET CONTACT",
	federkontakt: "SPRING CONTACT",
	flachkontakt: "FLAT CONTACT",
	flachstecker: "BLADE TERMINAL",
	flachkontaktgehaeuse: "FLAT CONTACT HOUSING",
	steckhuelse: "RECEPTACLE",
	kontaktsicherung: "CONTACT RETAINER",
	blindstopfen: "BLANKING PLUG",
	verschlusstopfen: "SEALING PLUG",
	einzeladerabdichtung: "SINGLE WIRE SEAL",
	dichtungseinsatz: "SEAL INSERT",
	dichtstreifen: "SEALING STRIP",
	dichtung: "SEAL",
	gehaeuse: "HOUSING",
	kabelbinder: "CABLE TIE",
	geflechtschlauch: "BRAIDED SLEEVE",
	schrumpfschlauchstueck: "HEAT SHRINK TUBING PIECE",
	wellrohr: "CORRUGATED CONDUIT",
	rohr: "PIPE",
	tuelle: "GROMMET",
	buchse: "BUSHING",
	scheibe: "WASHER",
	ausgleichscheibe: "SHIM",
	spreizniet: "EXPANDING RIVET",
	befestigungssockel: "MOUNTING BASE",
	befestigungsclip: "FASTENING CLIP",
	blechklammer: "SHEET METAL CLIP",
	"e-kupplung": "E-COUPLING",
	"schwingsitz-einbau": "SUSPENSION SEAT INSTALLATION",
	"bordausstattung-einbau": "ONBOARD EQUIPMENT INSTALLATION",
	verkleidung: "TRIM PANEL",
	hinweis: "NOTE",
	"abweichungs-erlaubnis": "DEVIATION PERMIT",
	verkaufsgruppe: "SALES GROUP",
	geometrie: "GEOMETRY",
	einzeldichtungssystem: "SINGLE SEAL SYSTEM",

	// Abbreviations
	zsb: "ASSEMBLY",
	rhm: "FRAME",
	fhs: "CAB",
	vkgr: "SALES GROUP",

	// Positions / structure
	rahmen: "FRAME",
	dach: "ROOF",
	bug: "FRONT",
	rumpf: "BODY",
	rechts: "RIGHT",
	links: "LEFT",
	li: "LEFT",
	re: "RIGHT",
	breit: "WIDE",
	schmal: "NARROW",

	// Colours
	sw: "BLACK",
	weiss: "WHITE",
	natur: "NATURAL",
	grau: "GRAY",
	gelb: "YELLOW",
	rot: "RED",
	hellblau: "LIGHT BLUE",

	// Connective words
	fuer: "FOR",
	ohne: "WITHOUT",
	mit: "WITH",
	inhalt: "CONTENT",
	teile: "PARTS",
	typ: "TYPE",
	bandware: "REEL GOODS",
	nachgerollt: "RE-ROLLED",
	verschliessbar: "LOCKABLE",
};

// Split a token into a leading prefix, a core word, and a trailing suffix of
// non-letter characters so punctuation like commas or parentheses is preserved.
function translateToken(token) {
	const match = token.match(/^([^\wÄÖÜäöüß]*)(.*?)([^\wÄÖÜäöüß]*)$/);
	if (!match) return token;
	const [, prefix, core, suffix] = match;
	const replacement = TERM_MAP[core.toLowerCase()];
	if (!replacement) return token;
	return `${prefix}${replacement}${suffix}`;
}

/**
 * Translate a German part description to English.
 *
 * @param {string} description - The original (German) description.
 * @param {string} language - Active UI language code ("EN", "DE", "SE").
 * @returns {string} Translated description for EN, otherwise the original.
 */
export function translatePartDescription(description, language) {
	if (language !== "EN" || !description || typeof description !== "string") {
		return description;
	}

	const trimmed = description.trim();
	if (KNOWN_DESCRIPTIONS[trimmed]) {
		return KNOWN_DESCRIPTIONS[trimmed];
	}

	// Token-level fallback: preserve original spacing by splitting on spaces.
	return description
		.split(" ")
		.map((token) => (token === "" ? token : translateToken(token)))
		.join(" ");
}

export default translatePartDescription;
